import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Plus, Trash2, AlertTriangle, X, Tag } from "lucide-react";
import api, { extractErrorMessage } from "../lib/api";
import Modal from "./Modal";
import { fmtPct } from "../lib/format";

function fmtQty(n) {
    return Number(n || 0).toLocaleString();
}

const todayIso = () => new Date().toISOString().slice(0, 10);
const newUid = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `g-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function RecordProductionModal({ order, onClose, onSaved }) {
    const activeLines = useMemo(
        () => (order.lines || []).filter((l) => l.active),
        [order],
    );
    const [orderLineId, setOrderLineId] = useState(activeLines[0]?.id || "");
    const [productionDate, setProductionDate] = useState(todayIso());
    const [palletGroups, setPalletGroups] = useState([
        { _uid: newUid(), pallets: 1, units_per_pallet: 0 },
    ]);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Round-23 — Issued Load Tags / Reserved Pallets state.
    // When an order has reserved pallets (advance LTs printed before RP),
    // the backend refuses RP creation unless we either link them or
    // explicitly opt out with `create_new_anyway + create_new_reason`.
    const [reservedPallets, setReservedPallets] = useState([]);
    const [linkPalletIds, setLinkPalletIds] = useState(new Set());
    const [createNewAnyway, setCreateNewAnyway] = useState(false);
    const [createNewReason, setCreateNewReason] = useState("");
    const [palletQtyOverrides, setPalletQtyOverrides] = useState({}); // {pallet_id: units}

    // Tolerance breach state
    const [breach, setBreach] = useState(null);
    const [overrideAck, setOverrideAck] = useState(false);
    const [overrideReason, setOverrideReason] = useState("");

    const loadReserved = useCallback(async () => {
        if (!order?.id) return;
        try {
            const { data } = await api.get(`/orders/${order.id}/pallets`,
                { params: { include_voided: false } });
            const reserved = (data || []).filter((p) => p.status === "reserved"
                && (!orderLineId || p.order_line_id === orderLineId));
            setReservedPallets(reserved);
            // Default-select all reserved pallets so the operator's first
            // click links them; opt-out is the explicit checkbox below.
            setLinkPalletIds(new Set(reserved.map((p) => p.id)));
        } catch (err) {
            // Non-fatal — the panel just won't render reserved pallets.
            console.warn("Failed to load reserved pallets", err);
        }
    }, [order?.id, orderLineId]);

    useEffect(() => { loadReserved(); }, [loadReserved]);

    useEffect(() => {
        if (!orderLineId) {
            setPreview(null);
            return;
        }
        api.get(`/orders/${order.id}/rp/preview`, {
            params: { order_line_id: orderLineId },
        })
            .then((r) => setPreview(r.data))
            .catch((e) => setError(extractErrorMessage(e)));
    }, [orderLineId, order.id]);

    const thisEntryUnits = useMemo(
        () =>
            palletGroups.reduce(
                (s, g) =>
                    s + (Number(g.pallets) || 0) * (Number(g.units_per_pallet) || 0),
                0,
            ),
        [palletGroups],
    );
    const thisEntryPallets = useMemo(
        () => palletGroups.reduce((s, g) => s + (Number(g.pallets) || 0), 0),
        [palletGroups],
    );

    const addGroup = () =>
        setPalletGroups((g) => [...g, { _uid: newUid(), pallets: 1, units_per_pallet: 0 }]);
    const removeGroup = (idx) =>
        setPalletGroups((g) => g.filter((_, i) => i !== idx));
    const updateGroup = (idx, patch) =>
        setPalletGroups((g) =>
            g.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
        );

    const linkedReserved = reservedPallets.filter((p) => linkPalletIds.has(p.id));
    const totalLinkedUnits = linkedReserved.reduce((sum, p) => {
        const q = palletQtyOverrides[p.id] ?? p.units_per_pallet ?? 0;
        return sum + (Number(q) || 0);
    }, 0);

    const submit = async (override = false, reason = "") => {
        setError("");
        if (!orderLineId) return setError("Select a product line.");
        if (!productionDate) return setError("Production date is required.");

        const haveLinkedReserved = linkedReserved.length > 0;

        if (!haveLinkedReserved) {
            // Manual pallet groups path — must add at least one group.
            if (thisEntryUnits <= 0)
                return setError("Total units must be greater than zero.");
            for (const g of palletGroups) {
                if (!Number(g.pallets) || !Number(g.units_per_pallet))
                    return setError(
                        "Each pallet group must have pallets ≥ 1 and units per pallet ≥ 1.",
                    );
            }
        } else {
            // Round-23 — every linked reserved pallet must have a confirmed
            // qty. Backend will reject otherwise.
            for (const p of linkedReserved) {
                const q = palletQtyOverrides[p.id] ?? p.units_per_pallet ?? 0;
                if (!Number(q) || Number(q) <= 0) {
                    return setError(
                        `Pallet ${p.pallet_number} has no quantity. Enter `
                        + `units per pallet for every issued tag you're `
                        + `linking, then retry.`,
                    );
                }
            }
            // If reserved pallets exist but the operator UNCHECKED them all,
            // make sure they've ticked "create new anyway" + provided a reason.
            if (linkedReserved.length === 0
                && reservedPallets.length > 0
                && !createNewAnyway) {
                return setError(
                    `This order has ${reservedPallets.length} reserved pallet `
                    + `tag(s) from previously printed Load Tags. Either link `
                    + `at least one, or tick "Create new pallet IDs anyway" `
                    + `and provide a reason.`,
                );
            }
        }
        setSubmitting(true);
        try {
            const payload = {
                production_date: productionDate,
                order_line_id: orderLineId,
                tolerance_override: override,
                tolerance_override_reason: reason,
            };

            if (haveLinkedReserved) {
                // Send the issued pallet IDs + their confirmed qty so the
                // backend can transition reserved → produced atomically.
                payload.link_reserved_pallet_ids = linkedReserved.map((p) => p.id);
                // Build pallet_groups from the confirmed qty per pallet — the
                // backend uses these as the canonical produced totals.
                payload.pallet_groups = linkedReserved.map((p) => ({
                    pallets: 1,
                    units_per_pallet: Number(
                        palletQtyOverrides[p.id] ?? p.units_per_pallet ?? 0,
                    ),
                }));
            } else {
                payload.pallet_groups = palletGroups.map((g) => ({
                    pallets: Number(g.pallets),
                    units_per_pallet: Number(g.units_per_pallet),
                }));
                if (createNewAnyway) {
                    payload.create_new_anyway = true;
                    payload.create_new_reason = createNewReason.trim();
                }
            }
            const { data } = await api.post(
                `/orders/${order.id}/rp`,
                payload,
            );
            onSaved?.(data);
        } catch (err) {
            if (
                err?.response?.status === 409 &&
                err?.response?.data?.detail?.type === "tolerance_breach"
            ) {
                setBreach(err.response.data.detail);
                setOverrideAck(false);
                setOverrideReason("");
            } else {
                setError(extractErrorMessage(err));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={`Record Production · ${order.internal_number}`}
            onClose={onClose}
            size="lg"
            testId="rp-modal"
        >
            <div className="space-y-4">
                {/* Pre-RP info */}
                {preview && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <Stat label="Ordered" value={fmtQty(preview.ordered_qty)} />
                        <Stat
                            label="Already Produced"
                            value={fmtQty(preview.already_produced)}
                            tone="neutral"
                        />
                        <Stat
                            label="Remaining"
                            value={fmtQty(preview.remaining)}
                            tone={preview.remaining === 0 ? "open" : "near"}
                        />
                        <Stat
                            label="Current Status"
                            value={
                                preview.current_status === "ok"
                                    ? "OK"
                                    : preview.current_status === "overrun"
                                      ? `Overrun ${fmtPct(preview.current_variance_pct)}`
                                      : `Underrun ${fmtPct(preview.current_variance_pct)}`
                            }
                            tone={
                                preview.current_status === "overrun"
                                    ? "past"
                                    : preview.current_status === "underrun"
                                      ? "near"
                                      : "open"
                            }
                        />
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Product / Order Line
                        </label>
                        <select
                            value={orderLineId}
                            onChange={(e) => setOrderLineId(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                            data-testid="rp-line"
                        >
                            {activeLines.map((l) => (
                                <option key={l.id} value={l.id}>
                                    L{l.line_no} · {l.product_snapshot?.name} ·{" "}
                                    {fmtQty(l.qty_ordered)} ord / {fmtQty(l.produced_qty)} prod
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                            Production Date
                        </label>
                        <input
                            type="date"
                            value={productionDate}
                            onChange={(e) => setProductionDate(e.target.value)}
                            className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                            data-testid="rp-date"
                        />
                    </div>
                </div>

                {reservedPallets.length > 0 && (
                    <div
                        className="border border-amber-300 bg-amber-50/50 rounded-sm p-3 space-y-2"
                        data-testid="rp-issued-tags-panel"
                    >
                        <div className="flex items-center justify-between">
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-900 inline-flex items-center gap-1.5">
                                <Tag className="w-3.5 h-3.5" />
                                Issued Load Tags · {reservedPallets.length} reserved pallet(s)
                            </h4>
                            <span className="text-xs text-amber-700 odr-mono">
                                Linked: {linkedReserved.length} ·{" "}
                                {fmtQty(totalLinkedUnits)} units
                            </span>
                        </div>
                        <p className="text-xs text-amber-800">
                            Pre-printed Load Tags exist for this order. Select which
                            tags you produced and confirm units per pallet — the
                            same PLL numbers will flow through the rest of the
                            order. Untick a row to skip it.
                        </p>

                        <table className="w-full text-xs odr-mono">
                            <thead>
                                <tr className="text-left text-amber-900 border-b border-amber-200">
                                    <th className="py-1 pr-2">Use</th>
                                    <th className="py-1 pr-2">PLL</th>
                                    <th className="py-1 pr-2">Planned</th>
                                    <th className="py-1 pr-2 text-right">Actual units</th>
                                    <th className="py-1 pr-2">Source</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reservedPallets.map((p) => {
                                    const checked = linkPalletIds.has(p.id);
                                    const v = palletQtyOverrides[p.id] ?? p.units_per_pallet ?? 0;
                                    return (
                                        <tr
                                            key={p.id}
                                            className="border-b border-amber-100 last:border-0"
                                            data-testid={`rp-issued-row-${p.pallet_number}`}
                                        >
                                            <td className="py-1 pr-2">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={(e) => {
                                                        const next = new Set(linkPalletIds);
                                                        if (e.target.checked) next.add(p.id);
                                                        else next.delete(p.id);
                                                        setLinkPalletIds(next);
                                                    }}
                                                    data-testid={`rp-issued-check-${p.pallet_number}`}
                                                />
                                            </td>
                                            <td className="py-1 pr-2">{p.pallet_number}</td>
                                            <td className="py-1 pr-2">
                                                {fmtQty(p.units_per_pallet)}
                                            </td>
                                            <td className="py-1 pr-2 text-right">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={v}
                                                    onChange={(e) =>
                                                        setPalletQtyOverrides((o) => ({
                                                            ...o,
                                                            [p.id]: e.target.value,
                                                        }))
                                                    }
                                                    disabled={!checked}
                                                    className="w-24 rounded-sm border border-amber-300 px-2 py-0.5 text-right odr-mono disabled:opacity-50"
                                                    data-testid={`rp-issued-qty-${p.pallet_number}`}
                                                />
                                            </td>
                                            <td className="py-1 pr-2 text-amber-700">
                                                {p.source === "advance_lt"
                                                    ? "Advance LT"
                                                    : (p.source || "LT")}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        <label className="flex items-start gap-2 text-xs text-amber-900 pt-2 border-t border-amber-200">
                            <input
                                type="checkbox"
                                checked={createNewAnyway}
                                onChange={(e) => {
                                    setCreateNewAnyway(e.target.checked);
                                    if (e.target.checked) setLinkPalletIds(new Set());
                                    else setLinkPalletIds(
                                        new Set(reservedPallets.map((p) => p.id)),
                                    );
                                }}
                                className="mt-0.5"
                                data-testid="rp-create-new-anyway"
                            />
                            <span>
                                <strong>Ignore issued tags</strong> — create brand-new pallet IDs
                                instead (audited). Requires a reason.
                            </span>
                        </label>
                        {createNewAnyway && (
                            <input
                                type="text"
                                value={createNewReason}
                                onChange={(e) => setCreateNewReason(e.target.value)}
                                placeholder="Reason for creating new pallet IDs (≥ 5 chars)"
                                className="w-full rounded-sm border border-amber-400 px-2 py-1 text-xs"
                                data-testid="rp-create-new-reason"
                            />
                        )}
                    </div>
                )}

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                            {reservedPallets.length > 0
                                ? "Manual Pallet Groups (only if not using issued tags)"
                                : "Pallet Groups"}
                        </h4>
                        <button
                            type="button"
                            onClick={addGroup}
                            className="text-sm text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                            data-testid="rp-add-group"
                        >
                            <Plus className="w-4 h-4" /> Add group
                        </button>
                    </div>
                    <div className="space-y-2">
                        {palletGroups.map((g, idx) => {
                            const subtotal =
                                (Number(g.pallets) || 0) *
                                (Number(g.units_per_pallet) || 0);
                            return (
                                <div
                                    key={g._uid || idx}
                                    className="grid grid-cols-12 gap-2 items-center"
                                    data-testid={`rp-group-${idx}`}
                                >
                                    <label className="col-span-3 text-xs text-gray-600">
                                        Pallets
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={g.pallets}
                                        onChange={(e) =>
                                            updateGroup(idx, { pallets: e.target.value })
                                        }
                                        className="col-span-2 odr-input rounded-sm border border-gray-300 px-2 text-sm text-right odr-mono"
                                        data-testid={`rp-group-pallets-${idx}`}
                                    />
                                    <span className="col-span-1 text-center text-gray-500">
                                        @
                                    </span>
                                    <input
                                        type="number"
                                        min="1"
                                        value={g.units_per_pallet}
                                        onChange={(e) =>
                                            updateGroup(idx, { units_per_pallet: e.target.value })
                                        }
                                        className="col-span-2 odr-input rounded-sm border border-gray-300 px-2 text-sm text-right odr-mono"
                                        placeholder="units"
                                        data-testid={`rp-group-units-${idx}`}
                                    />
                                    <span className="col-span-3 text-right odr-mono text-sm">
                                        = {fmtQty(subtotal)}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeGroup(idx)}
                                        disabled={palletGroups.length === 1}
                                        className="col-span-1 text-gray-500 hover:text-red-700 disabled:opacity-30"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-3 text-sm text-gray-700 flex items-center justify-between border-t border-gray-200 pt-3">
                        <span className="odr-mono">
                            {thisEntryPallets} pallet(s)
                        </span>
                        <span className="font-semibold odr-mono">
                            Entry total: {fmtQty(thisEntryUnits)} units
                        </span>
                    </div>
                </div>

                {error && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                        data-testid="rp-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => submit(false)}
                        disabled={submitting}
                        className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-60"
                        data-testid="rp-submit"
                    >
                        {submitting ? "Saving…" : "Save RP Entry"}
                    </button>
                </div>
            </div>

            {breach && (
                <Modal
                    title={
                        breach.tolerance_status === "overrun"
                            ? "Overrun warning"
                            : "Underrun warning"
                    }
                    onClose={() => setBreach(null)}
                    size="md"
                    testId="rp-breach-modal"
                >
                    <div className="space-y-3 text-sm text-gray-800">
                        <div className="flex gap-3 items-start">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                            <div className="space-y-2">
                                <p>
                                    Recording this production will put the order{" "}
                                    <span className="font-semibold">
                                        {breach.tolerance_status}
                                    </span>{" "}
                                    by{" "}
                                    <span className="odr-mono font-semibold">
                                        {fmtPct(breach.variance_pct)}
                                    </span>
                                    .
                                </p>
                                <ul className="text-xs text-gray-700 list-disc ml-5 space-y-0.5">
                                    <li>
                                        Ordered: <span className="odr-mono">{fmtQty(breach.ordered_qty)}</span>
                                    </li>
                                    <li>
                                        Already produced:{" "}
                                        <span className="odr-mono">
                                            {fmtQty(breach.already_produced)}
                                        </span>
                                    </li>
                                    <li>
                                        This entry:{" "}
                                        <span className="odr-mono">
                                            {fmtQty(breach.this_entry_units)}
                                        </span>
                                    </li>
                                    <li>
                                        Cumulative after:{" "}
                                        <span className="odr-mono font-semibold">
                                            {fmtQty(breach.cumulative_after)}
                                        </span>
                                    </li>
                                    <li>
                                        Allowed: +{fmtPct(breach.allowed_overrun_pct)} /
                                        -{fmtPct(breach.allowed_underrun_pct)}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="border-t border-gray-200 pt-3 space-y-2">
                            <label className="flex items-start gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={overrideAck}
                                    onChange={(e) => setOverrideAck(e.target.checked)}
                                    className="mt-0.5"
                                    data-testid="rp-override-ack"
                                />
                                <span>I understand and want to proceed anyway.</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Reason (optional, recorded in audit trail)"
                                value={overrideReason}
                                onChange={(e) => setOverrideReason(e.target.value)}
                                className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                                data-testid="rp-override-reason"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => setBreach(null)}
                                className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1"
                                data-testid="rp-breach-cancel"
                            >
                                <X className="w-4 h-4" /> Cancel
                            </button>
                            <button
                                type="button"
                                disabled={!overrideAck || submitting}
                                onClick={() => {
                                    setBreach(null);
                                    submit(true, overrideReason);
                                }}
                                className="odr-btn rounded-sm bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 disabled:opacity-50"
                                data-testid="rp-breach-proceed"
                            >
                                Proceed Anyway
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </Modal>
    );
}

function Stat({ label, value, tone = "neutral" }) {
    const tones = {
        neutral: "bg-white border-gray-200 text-gray-900",
        open: "bg-green-50 border-green-200 text-green-900",
        near: "bg-amber-50 border-amber-200 text-amber-900",
        past: "bg-red-50 border-red-200 text-red-900",
    };
    return (
        <div className={`border rounded-sm p-2 ${tones[tone]}`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
                {label}
            </div>
            <div className="mt-0.5 text-sm font-bold odr-mono">{value}</div>
        </div>
    );
}
